import { useState } from "react";
import { RecurringGameGroup } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface EditRecurringSheetProps {
  group: RecurringGameGroup;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const EditRecurringSheet = ({ group, open, onOpenChange, onSuccess }: EditRecurringSheetProps) => {
  const [name, setName] = useState(group.name);
  const [bet, setBet] = useState(group.instances[0]?.bet || "");
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Game name is required",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Fetch all non-archived instances in this group
      const { data: instances, error: fetchError } = await supabase
        .from("games")
        .select("id, start_date, end_date")
        .eq("recurring_group_id", group.recurringGroupId);

      if (fetchError) throw fetchError;

      // Filter out soft-deleted instances (epoch end_date = archived)
      const activeInstances = (instances || []).filter(
        (g) => !g.end_date || new Date(g.end_date).getFullYear() > 1970
      );

      // Batch update each instance: new base name + year suffix, new bet
      for (const instance of activeInstances) {
        const year = new Date(instance.start_date).getUTCFullYear();
        const instanceName = `${name.trim()} ${year}`;

        const { error: updateError } = await supabase
          .from("games")
          .update({
            name: instanceName,
            bet: bet.trim() || null,
          })
          .eq("id", instance.id);

        if (updateError) throw updateError;
      }

      toast({
        title: "Settings Updated",
        description: `Updated ${activeInstances.length} game instance${activeInstances.length !== 1 ? "s" : ""}.`,
      });

      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error("Error updating recurring game settings:", error);
      toast({
        title: "Error",
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Edit Recurring Settings</SheetTitle>
          <SheetDescription>
            Changes apply to all active instances in this group.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          <div className="space-y-2">
            <Label htmlFor="recurring-name">Game Name</Label>
            <Input
              id="recurring-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter game name"
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              Year suffix is added automatically (e.g. "{name} 2026")
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="recurring-bet">Bet (optional)</Label>
            <Input
              id="recurring-bet"
              value={bet}
              onChange={(e) => setBet(e.target.value)}
              placeholder="e.g. $20 each"
            />
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default EditRecurringSheet;
